<script>
  //import { fade, fly } from 'svelte/transition'
  import { Spring } from 'svelte/motion'
  //import { flip } from 'svelte/animate'
  //import { cubicIn, cubicOut } from 'svelte/easing'
  import { sheetJson } from '$state/data'
  import Wheel from './Wheel.svelte'

  let { index = 0, offset = 0 } = $props()

  // ── Fast-scroll detection ─────────────────────────────────────────────
  let prevIndex = index
  let lastIndexTime = 0
  let isFastScroll = $state(false)

  $effect.pre(() => {
    const now = performance.now()
    const jumped = Math.abs(index - prevIndex) > 1
    const rapid = now - lastIndexTime < 350
    isFastScroll = jumped || rapid
    prevIndex = index
    lastIndexTime = now
  })

  let atItems = $derived(
    Array.isArray(sheetJson.data)
      ? [
          ...new Map(
            sheetJson.data.filter((d) => d['has_@'] === true).map((d) => [d['bto-name'], d]),
          ).values(),
        ]
      : [],
  )

  let canberraAtItems = $derived.by(() => {
    const base = atItems.filter((d) => d['area-name'] === 'Canberra')
    const ebIdx = base.findIndex((d) => (d['bto-name'] ?? '').toLowerCase().startsWith('eastbank'))
    const sorted =
      ebIdx > 0 ? [base[ebIdx], ...base.slice(0, ebIdx), ...base.slice(ebIdx + 1)] : base
    return sorted.map((item) => ({
      ...item,
      _prefix: (item['bto-name'] ?? '').split('@')[0]?.trim() ?? '',
    }))
  })

  // 0 → 1 drives Canberra/Vista drawing apart automatically when offset >= 0.25
  const splitSpring = new Spring(0, { stiffness: 0.25, damping: 0.8 })
  $effect(() => {
    splitSpring.target = index > 0 || (index === 0 && offset >= 0.25) ? 1 : 0
  })

  let caberraLeft = $state(0)
  let vistaRight = $state(0)
  let eastBankLeft = $state(0)

  let btogridW = $state(1)
  let canberraW = $state(1)
  let canberraTextW = $state(1)
  let vistaW = $state(1)
  let vistaTextW = $state(1)
  let eastBankW = $state(1)
  let atLeft = $state(0)

  let centerGap = $derived(btogridW * 0.03)

  // Calculate internal padding inside the blocks since text is centered
  let canberraPaddingR = $derived(Math.max(0, (canberraW - canberraTextW) / 2))
  let vistaPaddingL = $derived(Math.max(0, (vistaW - vistaTextW) / 2))

  // 14px is roughly one blank space symbol at desktop font size, 6px at mobile font size
  let textSpace = $derived(btogridW < 768 ? 12 : 14)
  let initialBlockGap = $derived(textSpace - canberraPaddingR - vistaPaddingL)

  let initialLeftGap = $derived((btogridW - canberraW - vistaW - initialBlockGap) / 2)
  let splitLeftGap = $derived((btogridW - canberraW - vistaW - centerGap) / 2)

  // Correction to center the text visually since the blocks have asymmetric padding
  let centerCorrection = $derived((vistaPaddingL - canberraPaddingR) / 2)

  $effect(() => {
    if (index === 0 && offset < 0.25) {
      caberraLeft = initialLeftGap + centerCorrection
      vistaRight = initialLeftGap - centerCorrection
      eastBankLeft = -2000
      atLeft = btogridW / 2
    } else if (index === 0 && offset >= 0.25) {
      caberraLeft = Math.max(16, splitLeftGap - 100)
      vistaRight = Math.max(16, splitLeftGap - 100)
      eastBankLeft = -2000
      atLeft = btogridW / 2
    } else if (index === 1 && offset < 0.6) {
      let safeCenterGap = Math.min(
        centerGap,
        Math.max(4, (btogridW - 32 - eastBankW - canberraW) / 4),
      )
      let assemblyW = eastBankW + safeCenterGap * 4 + canberraW
      let assemblyLeft = (btogridW - assemblyW) / 2

      eastBankLeft = assemblyLeft
      caberraLeft = assemblyLeft + eastBankW + safeCenterGap * 4
      vistaRight = -100
      atLeft = assemblyLeft + eastBankW + safeCenterGap * 2
    } else if (index === 2 || (index === 1 && offset >= 0.6)) {
      const padding = btogridW < 768 ? 24 : 0
      caberraLeft = btogridW - canberraW - padding
      eastBankLeft = padding
      atLeft = btogridW / 2 + (btogridW < 768 ? 15 : 0)
    } else {
      caberraLeft = btogridW - canberraW
      eastBankLeft = (btogridW - eastBankW) / 2
      atLeft = btogridW / 2
    }
  })

  let splitProgress = $derived(
    index === 3 ? Math.min(1, Math.max(0, offset / 0.5)) : index > 3 ? 1 : 0,
  )

  let tEast = $derived(splitProgress * 1.2)
  let tWave = $derived(Math.max(0, splitProgress - 0.05) * 0.8)
  let isVistaDescriptiveActive = $derived(index === 0 && offset >= 0.25)

  let flyTargets = $state({ East: { x: 0, y: 150 }, Wave: { x: 0, y: 150 } })

  $effect(() => {
    let _ = offset // force re-evaluation as scroll happens
    if (index === 3 && typeof window !== 'undefined' && window.__wordPositions) {
      const e = window.__wordPositions.get('East')
      const w = window.__wordPositions.get('Wave')
      if (e) flyTargets.East = e
      if (w) flyTargets.Wave = w
    }
  })
</script>

<div class="bto-display">
  <div class="bto-card-layer">
    <div class="bto-grid" bind:clientWidth={btogridW}>
      <span
        bind:clientWidth={eastBankW}
        class="bto-name-text abs"
        class:hide={index === 0}
        style="position: absolute; left: {eastBankLeft}px;"
      >
        <div
          style="opacity: {splitProgress > 0 ? 0 : 1}; pointer-events: {splitProgress > 0
            ? 'none'
            : 'auto'};"
        >
          <Wheel
            word="EastBank"
            isAccent={index >= 2}
            data={index >= 2 ? canberraAtItems : []}
            progress={index >= 3
              ? canberraAtItems.length - 1
              : index === 2
                ? (offset < 0.4 ? 0 : (offset - 0.4) / 0.6) * (canberraAtItems.length - 1)
                : 0}
          />
        </div>
        {#if splitProgress > 0}
          <div
            style="position: absolute; display: flex; white-space: nowrap; align-items: center; justify-content: center; inset: 0;"
          >
            <span
              style="transform: translate(calc({flyTargets.East.x}px * var(--t)), calc({flyTargets
                .East
                .y}px * var(--t))) scale(calc(1 - 0.6 * var(--t))); color: var(--scrolly-bg-accent-color, #cfff5e); opacity: calc(1 - var(--t) * 2.5);"
              style:--t={tEast}>East</span
            >
            <span
              style="transform: translate(calc({flyTargets.Wave.x}px * var(--t)), calc({flyTargets
                .Wave
                .y}px * var(--t))) scale(calc(1 - 0.6 * var(--t))); color: var(--scrolly-bg-accent-color, #cfff5e); opacity: calc(1 - var(--t) * 2.5);"
              style:--t={tWave}>Wave</span
            >
          </div>
        {/if}
        <span class="bto-width-spacer">Descriptive word</span>
        <span class="bto-underline" class:fade-left={index >= 2}></span>
        <span class="bto-part-label bottom" class:fade-left={index >= 2}>Descriptive word</span>
        <span class="bto-part-label top" class:hide={index < 2 || index >= 3}
          >Combination of two words</span
        >
      </span>

      <span
        bind:clientWidth={canberraW}
        class="bto-name-text abs"
        class:hide={index >= 3}
        style="position: absolute; left: {caberraLeft}px;"
      >
        <div style="display: grid; place-items: center;">
          <span style="grid-area: 1/1;" bind:clientWidth={canberraTextW}>Canberra</span>
          <span class="bto-width-spacer" style="grid-area: 1/1;">Town name</span>
        </div>
        <span
          class="bto-underline"
          class:hide={index === 0 && offset < 0.25}
          class:fade-right={index >= 2}
        ></span>
        <span
          class="bto-part-label bottom"
          class:hide={index === 0 && offset < 0.25}
          class:fade-right={index >= 2}>Town name</span
        >
      </span>

      <span
        class="bto-name-text bto-at-symbol"
        style="position: absolute; left: {atLeft}px; transform: translateX(-50%);"
        class:hide={index === 0 || index >= 3}
        class:accent={index >= 1}
        >@
      </span>

      <span
        bind:clientWidth={vistaW}
        class="bto-name-text abs"
        class:hide={index > 0}
        class:accent={isVistaDescriptiveActive}
        style="position: absolute; right: {vistaRight}px;"
      >
        <div style="display: grid; place-items: center;">
          <span style="grid-area: 1/1;" bind:clientWidth={vistaTextW}>Vista</span>
          <span class="bto-width-spacer" style="grid-area: 1/1;">Descriptive word</span>
        </div>
        <span
          class="bto-underline"
          class:hide={index === 0 && offset < 0.25}
          class:fade-right={index >= 2}
          class:accent={isVistaDescriptiveActive}
        ></span>
        <span
          class="bto-part-label bottom"
          class:hide={index === 0 && offset < 0.25}
          class:fade-right={index >= 2}
          class:accent={isVistaDescriptiveActive}>Descriptive word</span
        >
      </span>
    </div>
  </div>
</div>

<style lang="scss">
  //* {
  //  outline: solid 1px red;
  //}

  .bto-display {
    position: absolute;
    inset: 0;
    contain: content;

    .bto-card-layer {
      position: absolute;
      inset: 0;
      display: flex;
      align-items: center;
      justify-content: center;

      //padding: 2rem;
      //opacity: calc(1 - var(--move-exit-t, 0));
      //transform: translateY(calc(var(--move-exit-t, 0) * -15vh));

      .bto-grid {
        position: relative;
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: center;
        background-color: #cfff5e11;
        width: 100%;
        max-width: 700px;
        text-align: center;
        //grid-template-columns:
        //  var(--bto-word-col-width, 14rem)
        //  auto
        //  var(--bto-word-col-width, 14rem);
        align-items: center;
        //column-gap: 1rem;

        //@media (max-width: 768px) {
        //  /* Tweak this value to adjust the gap between words when they split on mobile! */
        //  column-gap: 15rem;
        //}

        //&.highlighted {
        //  .bto-underline.accent,
        //  .bto-part-label.accent {
        //    color: var(--scrolly-bg-accent-color, var(--project-highlight-color, #cfff5e));
        //  }
        //}

        //&.exiting {
        //  .bto-queue-header {
        //    opacity: 0 !important;
        //    pointer-events: none;
        //  }
        //  .bto-at-symbol {
        //    opacity: 0 !important;
        //    pointer-events: none;
        //  }
        //  .col-3.bto-part {
        //    opacity: 0 !important;
        //    pointer-events: none;
        //  }
        //}
      }
    }
  }

  //shared grid

  .bto-grid-queue {
    grid-template-columns: 22rem auto 22rem;
    column-gap: 4rem;
  }

  .bto-name-text {
    position: relative;
    font-family: $font-selane-twenty;
    font-size: clamp(1.75rem, 4.5vw, 3.5rem);
    font-weight: 400;
    line-height: 1.1;
    height: var(--item-h, 3.5rem);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    will-change: transform, opacity, left, right;
    transition:
      opacity 0.4s ease,
      left 0.4s ease,
      right 0.4s ease;
    //transform: translateX(calc(var(--x-dir, 0) * (1 - var(--split-t, 1)) * 4rem));

    //@media (max-width: 768px) {
    //  /* Tweak this 2.5rem value to adjust how close they are on mobile! */
    //  transform: translateX(calc(var(--x-dir, 0) * (1 - var(--split-t, 1)) * 1rem));
    //}

    &.hide {
      opacity: 0 !important;
    }

    &.accent {
      color: var(--scrolly-bg-accent-color, var(--project-highlight-color, #cfff5e));
    }
  }

  .bto-width-spacer {
    visibility: hidden;
    height: 0;
    pointer-events: none;
    font-family: var(--font-sans-serif);
    font-size: 20px;
    white-space: nowrap;
  }

  .bto-underline {
    position: absolute;
    --fade-slide-distance: 2rem;
    width: 100%;
    height: 1px;
    background: currentColor;
    left: 0;
    top: calc(100% + 0.15rem);
    opacity: 1;
    /* The underline no longer needs translation because its parent .bto-name-text translates it */
    transition:
      opacity 0.45s ease,
      color 0.4s ease-out,
      transform 0.45s ease;

    &.hide {
      opacity: 0 !important;
    }

    &.accent {
      color: var(--scrolly-bg-accent-color, var(--project-highlight-color, #cfff5e)) !important;
    }

    &.fade-left {
      opacity: 0 !important;
      transform: translateX(calc(var(--fade-slide-distance) * -1));
    }

    &.fade-right {
      opacity: 0 !important;
      transform: translateX(var(--fade-slide-distance));
    }
  }

  .bto-part-label {
    position: absolute;
    --fade-slide-distance: 2rem;
    min-width: max-content;
    left: 50%;
    font-family: var(--font-sans-serif);
    font-size: 20px;
    line-height: 1.4;
    text-align: center;
    color: #fff !important;
    opacity: 1;
    transform: translateX(-50%);
    transition:
      opacity 0.45s ease,
      color 0.4s ease-out,
      transform 0.45s ease;

    &.top {
      bottom: calc(100% + 1rem);
      border-bottom: 1px solid currentColor;
      padding-bottom: 0.3rem;

      &.hide {
        opacity: 0 !important;
        transform: translate(-50%, -1rem) !important;
      }
    }

    &.bottom {
      top: calc(100% + 0.4rem);
      padding-top: 0.3rem;
    }

    &.hide {
      opacity: 0 !important;
    }

    &.accent {
      color: var(--scrolly-bg-accent-color, var(--project-highlight-color, #cfff5e)) !important;
    }

    &.fade-left {
      opacity: 0 !important;
      transform: translate(calc(-50% - var(--fade-slide-distance)), 0) !important;
    }

    &.fade-right {
      opacity: 0 !important;
      transform: translate(calc(-50% + var(--fade-slide-distance)), 0) !important;
    }
  }

  .bto-at-container {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .bto-at-symbol {
    font-family: $font-selane-twenty;
    font-size: clamp(1.75rem, 4.5vw, 3.5rem);
    font-weight: 400;
    color: var(--scrolly-bg-text-color, #fff);
    line-height: 1.1;
    align-self: center;
    padding: 0;
    text-align: center;

    @media (max-width: 767px) {
      margin-top: -0.15em;
    }

    &.accent {
      color: var(--scrolly-bg-accent-color, var(--project-highlight-color, #cfff5e));
    }
  }
  .ghost-div {
    height: 20px;
  }

  @media (max-width: 600px) {
    .bto-card-layer {
      padding: 1rem;
    }

    .bto-grid {
      grid-template-columns: auto auto auto;
      column-gap: 0.3rem;
    }

    .bto-part {
      transform: translateX(
        calc(
          var(--x-dir, 0) * (1 - var(--split-t, 1)) * 1.5rem - var(--x-dir, 0) *
            var(--spread-t, 0) * 1.5rem
        )
      );
    }

    .bto-grid-queue {
      grid-template-columns: 1.6fr 0.3fr 1fr;
    }

    .bto-queue-header {
      white-space: normal;
    }
    .bto-part-label {
      font-size: 14px;
    }
  }

  /* ── Card 3+: word queue ── */
  .bto-queue-col {
    position: relative;
  }

  .bto-queue-header {
    position: absolute;
    bottom: 100%;
    left: 0;
    padding-bottom: 1.25rem;
    white-space: nowrap;
    opacity: 1;
    transition: opacity 0.4s ease-out;
  }

  .bto-queue-title {
    display: block;
    font-family: var(--font-sans-serif);
    font-size: 20px;
    color: var(--scrolly-bg-accent-color, var(--project-highlight-color, #cfff5e));
    margin-bottom: 0.5rem;
  }

  .bto-queue-header-line {
    width: 14rem;
    height: 2px;
    background: var(--scrolly-bg-accent-color, var(--project-highlight-color, #cfff5e));
    @media screen and (max-width: 425px) {
      width: 10rem;
    }
  }

  .bto-queue-track {
    position: relative;
    height: var(--item-h, 3.5rem);
    overflow: visible;
    width: 100%;
    /* clip upward overflow (past-items drifting into header) while keeping downward cascade open */
    clip-path: inset(0 -999rem -999rem -999rem);
  }

  .bto-queue-item {
    position: absolute;
    left: 0;
    right: 0;
    top: calc((var(--i, 0) - var(--offset, 0)) * var(--item-h, 3.5rem));
    height: var(--item-h, 3.5rem);
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: max(0, 1 - max(0, var(--dist, 0)) * 0.35);
    font-size: calc(clamp(1.75rem, 4.5vw, 3.5rem) * max(0.65, 1 - max(0, var(--dist, 0)) * 0.1));
    line-height: 1.1;

    &.move-down {
      transform: translate(calc(var(--move-exit-t, 0) * 20vw), calc(var(--move-exit-t, 0) * 45vh));
    }
  }

  .bto-queue-word {
    font-family: $font-selane-twenty;
    font-weight: 400;
    color: var(--scrolly-bg-text-color, #fff);
    white-space: nowrap;
    transition: color 0.15s ease;

    &.accent {
      color: var(--scrolly-bg-accent-color, var(--project-highlight-color, #cfff5e));
    }

    &.split-compound {
      display: inline-flex;
      white-space: nowrap;

      .part-east {
        display: inline-block;
      }

      .part-second {
        display: inline-block;
        transform: translate(
          calc(var(--move-exit-t, 0) * -37vw),
          calc(var(--move-exit-t, 0) * -2vh)
        );
      }
    }
  }
  @media (max-width: 600px) {
    .bto-queue-title {
      font-size: 16px;
    }
    .bto-queue-header-line {
      width: 12rem;
    }
  }
</style>
